import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  input,
  InputSignal,
  Optional,
  Self,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';

import { take } from 'rxjs/operators';

import { fileToBase64 } from '@core/common-legacy';
import { getHumanReadableFileSize } from '@core/utils-legacy';

import { AvatarComponent } from '../../common/avatar/avatar.component';
import { ButtonComponent } from '../../common/button/components/button/button.component';
import { IconComponent } from '../../common/icon/icon.component';
import {
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_VALID_IMAGE_MIME_TYPES,
} from '../../utils/image-input-utils';
import { RtInput } from '../../utils/rt-input';

export type TranslationsKeysType = {
  cta: string;
  secondaryCta: string;
  typeAndSizeError: string;
  sizeError: string;
  limitError: string;
  fileDetails: string;
  remove: string;
  typeUnknownError: string;
};

export enum InputType {
  Avatar = 'avatar',
  Logo = 'logo',
}

@Component({
  selector: 'rt-image-input',
  templateUrl: './image-input.component.html',
  styleUrls: ['./image-input.component.less'],
  imports: [IconComponent, AvatarComponent, ButtonComponent, NgTemplateOutlet],
})
export class ImageInputComponent extends RtInput<File | string | null> {
  protected readonly TypeError = TypeError;
  protected readonly InputType = InputType;

  public readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  public readonly maxFileSize = input(DEFAULT_MAX_FILE_SIZE); // file size limit in bytes
  public readonly translations: InputSignal<TranslationsKeysType> = input.required();
  public readonly type: InputSignal<'avatar' | 'logo'> = input(InputType.Avatar);

  public readonly previewUrl: InputSignal<string | undefined | null> = input();

  public inputAcceptAttr: string = DEFAULT_VALID_IMAGE_MIME_TYPES.join(', ');
  public displayError: boolean = false;
  public errorMessage: string;

  public avatarPreview: string | null;

  constructor(
    private destroyRef: DestroyRef,
    protected cdRef: ChangeDetectorRef,
    @Optional()
    @Self()
    ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  public ngAfterViewInit(): void {
    this.setAvatarPreview(this.value);
    this.ngControl?.valueChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.setAvatarPreview(value);
    });
  }

  public setAvatarPreview(value: File | string | null): void {
    if (value) {
      value instanceof File
        ? this.setAvatarPreviewFromFile(value)
        : this.setAvatarPreviewFromUrl(value);
    } else {
      this.clearInput();
      this.avatarPreview = null;
    }
  }

  public onFileChange(input: HTMLInputElement): void {
    const file = input.files ? input.files[0] : undefined;

    if (!file) return;

    const validType = DEFAULT_VALID_IMAGE_MIME_TYPES.includes(file.type);
    const validSize = file.size <= this.maxFileSize();

    if (validType && validSize) {
      this.displayError = false;
      this.handleChange(file);
    } else {
      this.displayError = true;
      if (!validType && !validSize) {
        this.errorMessage = this.translations().typeAndSizeError;
      } else if (!validSize) {
        this.errorMessage = `${this.translations().limitError}  ${getHumanReadableFileSize(this.maxFileSize())}. ${this.translations().sizeError}  ${getHumanReadableFileSize(file.size)}.`;
      } else if (!validType) {
        this.errorMessage = this.translations().typeUnknownError;
      }
      this.cdRef.detectChanges();
    }
  }

  public clearInput() {
    const input = this.fileInput()?.nativeElement;
    if (input) input.value = '';
  }

  protected clearValue(input: HTMLInputElement): void {
    this.ngControl?.reset(null);
    this.displayError = false;
    this.avatarPreview = null;
    input.value = '';
    this.handleChange(null);
    this.cdRef.detectChanges();
  }

  private setAvatarPreviewFromFile(file: File): void {
    fileToBase64(file)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(base64 => {
        this.avatarPreview = base64;
        this.cdRef.detectChanges();
      });
  }

  private setAvatarPreviewFromUrl(url: string): void {
    this.avatarPreview = url;
    this.cdRef.detectChanges();
  }
}
